import React, { useRef, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import Button from '../UI/Button';

interface AvatarUploadProps {
  onUpload: (file: File) => Promise<void>;
  currentAvatarUrl?: string;
  className?: string;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  onUpload,
  currentAvatarUrl,
  className = '',
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentAvatarUrl || user?.profile?.avatar || null);
  
  // Update preview when user profile changes
  React.useEffect(() => {
    if (user?.profile?.avatar) {
      setPreview(user.profile.avatar);
    } else if (currentAvatarUrl) {
      setPreview(currentAvatarUrl);
    }
  }, [user?.profile?.avatar, currentAvatarUrl]);

  const getInitials = () => {
    if (user?.profile?.displayName) {
      return user.profile.displayName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    try {
      await onUpload(file);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar. Please try again.');
      setPreview(currentAvatarUrl || null);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <div className="relative">
        <div className="w-32 h-32 rounded-full overflow-hidden bg-[var(--color-brand-primary)] flex items-center justify-center text-white font-bold text-3xl">
          {preview ? (
            <img
              src={preview}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{getInitials()}</span>
          )}
        </div>
        {isUploading && (
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Upload avatar"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={isUploading}
      >
        {preview ? 'Change Avatar' : 'Upload Avatar'}
      </Button>
    </div>
  );
};

export default AvatarUpload;

